import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { DateRange } from "@/hooks/useDateRange";
import { analyticsReviewsService } from "@/services/analytics-reviews-service";
import type { ReviewAnalyticsTabApi } from "@/types/analytics-reviews";
import type { ReviewsSegment } from "../components/reviews/types";
import { SEGMENT_TO_REVIEW_TAB } from "../components/reviews/reviewsSegment";
import {
  getPreviousAnalyticsDateRange,
  toAnalyticsDateParam,
} from "../utils/analyticsDateUtils";
import { useAnalyticsOverviewRange } from "./useAnalyticsOverviewRange";

export const analyticsReviewsKeys = {
  all: ["analytics-reviews"] as const,
  overall: (
    hotelId: string,
    from: string,
    to: string,
    tab: ReviewAnalyticsTabApi,
  ) =>
    [...analyticsReviewsKeys.all, "overall", hotelId, from, to, tab] as const,
  distribution: (
    hotelId: string,
    from: string,
    to: string,
    tab: ReviewAnalyticsTabApi,
  ) =>
    [
      ...analyticsReviewsKeys.all,
      "distribution",
      hotelId,
      from,
      to,
      tab,
    ] as const,
  sourceSplit: (
    hotelId: string,
    from: string,
    to: string,
    tab: ReviewAnalyticsTabApi,
  ) =>
    [
      ...analyticsReviewsKeys.all,
      "source-split",
      hotelId,
      from,
      to,
      tab,
    ] as const,
  averageByCategory: (
    hotelId: string,
    from: string,
    to: string,
    tab: ReviewAnalyticsTabApi,
  ) =>
    [
      ...analyticsReviewsKeys.all,
      "average-by-category",
      hotelId,
      from,
      to,
      tab,
    ] as const,
  overTime: (
    hotelId: string,
    from: string,
    to: string,
    tab: ReviewAnalyticsTabApi,
  ) =>
    [...analyticsReviewsKeys.all, "over-time", hotelId, from, to, tab] as const,
};

function buildRangeQuery(
  hotelId: string,
  from: string,
  to: string,
  segment: ReviewsSegment,
) {
  return {
    hotelId,
    from,
    to,
    tab: SEGMENT_TO_REVIEW_TAB[segment],
  };
}

export function useReviewsAnalytics(
  dateRange: DateRange,
  segment: ReviewsSegment,
) {
  const rangeParams = useAnalyticsOverviewRange(dateRange);
  const { hotelId, from, to, isReady } = rangeParams;
  const query = buildRangeQuery(hotelId, from, to, segment);

  const previousRangeQuery = useMemo(() => {
    const previousRange = getPreviousAnalyticsDateRange(dateRange);
    const previousFrom = toAnalyticsDateParam(previousRange?.from);
    const previousTo = toAnalyticsDateParam(previousRange?.to);

    if (!previousFrom || !previousTo) {
      return null;
    }

    return buildRangeQuery(hotelId, previousFrom, previousTo, segment);
  }, [dateRange, hotelId, segment]);

  const overall = useQuery({
    queryKey: analyticsReviewsKeys.overall(hotelId, from, to, query.tab),
    queryFn: () => analyticsReviewsService.getOverall(query),
    enabled: isReady,
  });

  const distribution = useQuery({
    queryKey: analyticsReviewsKeys.distribution(hotelId, from, to, query.tab),
    queryFn: () => analyticsReviewsService.getDistribution(query),
    enabled: isReady,
  });

  const sourceSplit = useQuery({
    queryKey: analyticsReviewsKeys.sourceSplit(hotelId, from, to, query.tab),
    queryFn: () => analyticsReviewsService.getSourceSplit(query),
    enabled: isReady,
  });

  const averageByCategory = useQuery({
    queryKey: analyticsReviewsKeys.averageByCategory(
      hotelId,
      from,
      to,
      query.tab,
    ),
    queryFn: () => analyticsReviewsService.getAverageByCategory(query),
    enabled: isReady,
  });

  const overTime = useQuery({
    queryKey: analyticsReviewsKeys.overTime(hotelId, from, to, query.tab),
    queryFn: () => analyticsReviewsService.getOverTime(query),
    enabled: isReady,
  });

  const overallPrevious = useQuery({
    queryKey: previousRangeQuery
      ? analyticsReviewsKeys.overall(
          previousRangeQuery.hotelId,
          previousRangeQuery.from,
          previousRangeQuery.to,
          previousRangeQuery.tab,
        )
      : [...analyticsReviewsKeys.all, "overall-previous-disabled"],
    queryFn: () => analyticsReviewsService.getOverall(previousRangeQuery!),
    enabled: isReady && previousRangeQuery != null,
  });

  const queries = [
    overall,
    distribution,
    sourceSplit,
    averageByCategory,
    overTime,
    overallPrevious,
  ];

  return {
    ...rangeParams,
    tab: query.tab,
    overall,
    overallPrevious,
    distribution,
    sourceSplit,
    averageByCategory,
    overTime,
    isLoading: queries.some((item) => item.isLoading),
    isFetching: queries.some((item) => item.isFetching),
    isError: queries.some((item) => item.isError),
    error: queries.find((item) => item.error)?.error,
  };
}
